/**
 * Phase 8 · C-13 — Library UI types.
 *
 * These types live separately from `mockData.ts` so real product code (the
 * page + cards + drawer) never has to import a mock module. `mockData.ts`
 * re-imports from here purely for the static fixture shape.
 */

/** Sync status flag displayed on SourceCard / drawer. */
export type SyncStatus = 'synced' | 'syncing' | 'failed';

/** Filter chip states used by LibraryPage. */
export type SourceFilter = 'all' | 'active' | 'warn' | 'disconnected';

/** Map SyncStatus → SourceFilter bucket. */
export function syncStatusToFilter(s: SyncStatus): Exclude<SourceFilter, 'all'> {
  if (s === 'synced')  return 'active';
  if (s === 'syncing') return 'warn';
  return 'disconnected';
}

/**
 * UI shape consumed by `SourceCard`, `NetworkSettingsRail`, and
 * `LibrarySourceDetailPanel`. LibraryPage maps `LibrarySource` rows from
 * Supabase into this shape, backfilling display-only fields with sensible
 * defaults when the DB has no column for them.
 */
export interface LibraryNetwork {
  id: string;
  name: string;
  description: string;
  iconInitials: string;
  connectionCount: number;
  /** Candidate count sourced from this network */
  candidateCount: number;
  /** Member count with access to this network */
  memberCount: number;
  syncStatus: SyncStatus;
  uploadedBy: string;
  uploadedAt: string;
  /** ISO timestamp of last successful sync */
  lastSynced?: string;
  privacy: 'link' | 'members';
  directSearchUrl: string;
}

export interface LibraryMember {
  id: string;
  name: string;
  email: string;
  role: 'Owner' | 'Admin' | 'Member' | 'Viewer';
  connectionsUploaded: number;
  avatarUrl?: string;
}

export interface LibraryCandidate {
  id: string;
  name: string;
  title: string;
  uploadedBy: string;
  uploadedAt: string;
  linkedinUrl?: string;
}
